/**
 * ShiftRequestsPanel (BON-243) — the RD's inline review of who has raised a
 * hand for one open shift, expanded from its board row.
 *
 * Confirming is destructive to the other requesters (they're auto-declined and
 * emailed), so the confirm button carries that consequence in its own label
 * rather than hiding it behind a generic "Confirm".
 *
 * Split out of RegionalShiftBoard to keep that file under the lint size cap.
 */
import type { ShiftRequester } from '../../../api/openShifts';

/** "2h ago" / "3d ago" — how long someone has been waiting. */
export function waitedFor(requestedAtIso: string, now = new Date()): string {
  const mins = Math.floor((now.getTime() - new Date(requestedAtIso).getTime()) / 60_000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export interface ShiftRequestsPanelProps {
  requesters: ShiftRequester[];
  /** Read-only (borrowed region) hides both actions. */
  live: boolean;
  busy: boolean;
  onConfirm: (userId: string) => void;
  onDecline: (userId: string) => void;
}

export function ShiftRequestsPanel({
  requesters,
  live,
  busy,
  onConfirm,
  onDecline,
}: ShiftRequestsPanelProps) {
  if (requesters.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--nb-subtle)', padding: '8px 4px' }}>
        Nobody has requested this shift yet.
      </div>
    );
  }

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: '4px 0',
        display: 'grid',
        gap: 6,
      }}
    >
      {requesters.map((r) => (
        <li
          key={r.userId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--nb-surface)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{r.fullName}</span>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--nb-subtle)' }}>
              asked {waitedFor(r.requestedAtIso)}
              {/* No email on file means the decision can't reach them — say so
                  before the RD acts, not in a failure toast afterwards. */}
              {r.email === null && ' · no email on file'}
            </span>
          </span>
          {live && (
            <>
              <button
                type="button"
                className="nb-btn nb-btn-ghost"
                disabled={busy}
                onClick={() => onDecline(r.userId)}
              >
                Decline
              </button>
              <button
                type="button"
                className="nb-btn nb-btn-primary"
                disabled={busy}
                onClick={() => onConfirm(r.userId)}
              >
                {requesters.length > 1 ? 'Confirm (declines others)' : 'Confirm'}
              </button>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
